import React from 'react';
import { 
  GraduationCap, Star, Zap, Target, BookOpen, 
  ShieldCheck, Rocket, BrainCircuit, School, PieChart as PieChartIcon 
} from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from 'recharts';

const LISE_GROUPS = {
  'Fen Lisesi': { color: "#3b82f6", desc: "Sayısal mantık, analitik düşünce, bilimsel araştırma." },
  'Sosyal Bilimler Lisesi': { color: "#8b5cf6", desc: "Sözel beceriler, felsefi sorgulama, toplumsal analiz." },
  'Mesleki ve Teknik Anadolu Lisesi': { color: "#f59e0b", desc: "Pratik zeka, el becerisi, mesleki uzmanlık." },
  'Anadolu Lisesi': { color: "#10b981", desc: "Dengeli akademik eğitim, yabancı dil, geniş yelpaze." },
  'Güzel Sanatlar ve Spor Lisesi': { color: "#ec4899", desc: "Sanatsal duyarlılık, estetik, fiziksel performans." }
};

export const LgsPusulasiReportUI = ({ data, meta }) => {
  if (!data || !data.scores) return <div className="p-10 text-center font-bold">Veri bulunamadı.</div>;

  const chartData = Object.keys(data.scores)
    .filter(key => LISE_GROUPS[key])
    .map(key => ({
      name: key,
      score: data.scores[key],
      fill: LISE_GROUPS[key].color,
      desc: LISE_GROUPS[key].desc
    }))
    .sort((a, b) => b.score - a.score);

  const top1 = chartData[0] || { name: "Belirsiz", score: 0, fill: "#cbd5e1", desc: "-" };
  const top2 = chartData[1] || { name: "Belirsiz", score: 0, fill: "#cbd5e1", desc: "-" };
  const top3 = chartData[2] || { name: "Belirsiz", score: 0, fill: "#cbd5e1", desc: "-" };

  return (
    // Dikey boşlukları print modunda hafifçe daralttık (print:space-y-4)
    <div className="space-y-5 print:space-y-4 font-sans flex flex-col bg-white relative z-10 print:m-0 print:p-0">
      
      {/* ÜST BAŞLIK - KOMPAKT */}
      <div className="bg-slate-900 text-white p-4 print:p-3 rounded-2xl flex justify-between items-center shadow-sm shrink-0">
        <div className="flex items-center gap-4 print:gap-3">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center shadow-inner bg-indigo-600">
            <GraduationCap className="h-6 w-6 text-white"/>
          </div>
          <div>
            <h2 className="text-xl print:text-lg font-black leading-none mb-1">LGS Lise Türleri Yönelim Profili</h2>
            <span className="text-[10px] print:text-[9px] text-slate-400 font-bold tracking-widest uppercase block">
               {meta?.studentClass || "Sınıf Belirtilmemiş"} • NO: {meta?.studentNo || "-"} • {meta?.studentName || "İsimsiz Kayıt"}
            </span>
          </div>
        </div>
      </div>

      {/* ÜST PANEL: GRAFİK VE PODYUM */}
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-5 print:gap-4 shrink-0 avoid-break">
        
        {/* SOL: PASTA (DONUT) GRAFİĞİ VE LİSTE */}
        {/* Yükseklik A4 için özel olarak ayarlandı */}
        <div className="bg-white p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[320px] print:h-[290px] overflow-hidden">
          <h3 className="font-black text-slate-800 text-[11px] print:text-[10px] uppercase tracking-widest mb-3 flex items-center border-b pb-2 shrink-0">
            <PieChartIcon className="h-4 w-4 mr-2 text-indigo-600"/> Lise Türü Eğilim Dağılımı
          </h3>
          
          <div className="flex-1 flex items-center justify-between min-w-0 gap-3 mt-1">
            {/* SOL YARI: İÇİ BOŞ PASTA GRAFİĞİ */}
            <div className="w-[50%] h-full relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    innerRadius="60%"
                    outerRadius="90%"
                    paddingAngle={4}
                    dataKey="score"
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '11px', padding: '6px 10px' }} 
                    itemStyle={{ color: '#334155', fontWeight: 'bold' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">ÖNCELİKLİ</span>
                <span className="text-[11px] font-black text-slate-800 text-center px-2 leading-none mt-1">{top1.name}</span>
              </div>
            </div>

            {/* SAĞ YARI: LİSE TÜRLERİ LEJANT LİSTESİ */}
            <div className="w-[50%] flex flex-col gap-2 print:gap-1.5 justify-start pt-1">
              {chartData.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2.5 bg-slate-50 p-2 print:p-1.5 rounded-lg border border-slate-100">
                  <div className="h-3 w-3 print:h-2.5 print:w-2.5 rounded-full shrink-0" style={{ backgroundColor: item.fill }}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] print:text-[9.5px] font-bold text-slate-700 truncate leading-none mb-0.5">{item.name}</p>
                    <p className="text-[9px] print:text-[7.5px] text-slate-400 leading-none">{item.score} Puan</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SAĞ: ZİRVEDEKİ LİSE TÜRLERİ */}
        <div className="flex flex-col gap-4 print:gap-3">
          <div className="bg-indigo-50/40 p-5 print:p-4 rounded-2xl border border-indigo-100 shadow-sm flex-1 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 opacity-10 rounded-bl-full bg-indigo-600"></div>
            <span className="text-[10px] print:text-[9px] font-black uppercase tracking-widest text-indigo-600 mb-1.5 flex items-center">
              <Star className="h-4 w-4 mr-1.5 fill-current"/> 1. Baskın Yönelim
            </span>
            <h3 className="text-2xl print:text-xl font-black leading-tight text-slate-800 mb-1">{top1.name}</h3>
            <p className="text-[11px] print:text-[10px] text-slate-500 font-medium leading-relaxed">{top1.desc}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 print:gap-3">
            <div className="bg-slate-50 p-4 print:p-3 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-center">
                <span className="text-[9px] print:text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1.5 block">2. Yönelim</span>
                <h4 className="text-sm print:text-xs font-black text-slate-700 leading-tight">{top2.name}</h4>
            </div>
            <div className="bg-slate-50 p-4 print:p-3 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-center">
                <span className="text-[9px] print:text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1.5 block">3. Yönelim</span>
                <h4 className="text-sm print:text-xs font-black text-slate-700 leading-tight">{top3.name}</h4>
            </div>
          </div>
          
          <div className="bg-emerald-50/50 p-4 print:p-3 rounded-2xl border border-emerald-100 flex items-center gap-3">
             <ShieldCheck className="h-8 w-8 text-emerald-500 shrink-0"/>
             <p className="text-[11px] print:text-[10px] text-emerald-800 font-medium leading-snug">
               Öğrencinin ilgi, yetenek ve değerleri en yüksek oranda <strong>{top1.name}</strong> ile uyumludur.
             </p>
          </div>
        </div>
      </div>

      {/* ALT PANEL: UZMANLARA YÖNELİK STRATEJİK BİLGİLENDİRME KARTLARI */}
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-5 print:gap-4 shrink-0 avoid-break">
        
        {[
          { icon: BrainCircuit, title: "Akademik Dinamik", text: "Öğrencinin yönelim profili incelendiğinde, baskın olan okul türünün gerektirdiği düşünme yapısına (soyut/somut ya da analitik/sözel) güçlü bir yatkınlığı olduğu görülmektedir.", bg: "bg-indigo-50", color: "text-indigo-600" },
          { icon: School, title: "İdeal Öğrenme Ortamı", text: "Öğrencinin potansiyelini en üst düzeye çıkarabilmesi için seçilen okul türünün sunduğu fiziksel ve sosyal ortama (atölye, laboratuvar, proje tabanlı sınıflar) ihtiyaç duyduğu tespit edilmiştir.", bg: "bg-amber-50", color: "text-amber-600" },
          { icon: Target, title: "Gelişim ve Destek Odağı", text: "Tercih edilecek okulun gerektirdiği ders çalışma temposuna ve disiplinine şimdiden uyum sağlaması adına öz düzenleme becerileri desteklenmelidir.", bg: "bg-rose-50", color: "text-rose-600" },
          { icon: BookOpen, title: "Aile ve Öğretmen İşbirliği", text: "Aile beklentileri ile öğrencinin yeteneklerinin uyumu sağlanmalı; öğrenci, yeteneği olmayan ancak popüler olan okul türlerine karşı baskılanmamalıdır.", bg: "bg-emerald-50", color: "text-emerald-600" }
        ].map((card, idx) => (
          <div key={idx} className={`p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm bg-white h-[140px] print:h-[130px] overflow-hidden flex flex-col`}>
             <div className="flex items-center gap-3 mb-2.5">
               <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${card.bg}`}>
                 <card.icon className={`h-4 w-4 ${card.color}`}/>
               </div>
               <h4 className="font-bold text-slate-800 text-[11px] print:text-[10px] uppercase tracking-wider">{card.title}</h4>
             </div>
             <p className="text-[11px] print:text-[10px] text-slate-600 leading-relaxed text-justify line-clamp-3">
               {card.text}
             </p>
          </div>
        ))}

        {/* GENİŞ KART */}
        <div className="bg-slate-50 p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm col-span-1 md:col-span-2 print:col-span-2 flex items-center gap-4 h-[85px] print:h-[75px]">
           <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0">
             <Rocket className="h-5 w-5 text-indigo-600"/>
           </div>
           <div>
             <h4 className="font-bold text-slate-800 text-[10px] uppercase tracking-[0.2em] mb-1">Lise Geçiş Stratejisi ve Projeksiyon</h4>
             <p className="text-[11px] print:text-[10px] text-slate-700 leading-relaxed">
               Bu profil, lise dönemi boyunca akademik ve kişisel gelişim dengesini kurduğunda, üniversite geçişinde de kendi alanında yüksek başarı potansiyeli taşımaktadır. LGS tercih dönemi öncesi okul ziyaretleri tavsiye edilir.
             </p>
           </div>
        </div>
      </div>

      {/* ETİK UYARI */}
      <div className="p-3 print:p-2.5 bg-slate-50 rounded-xl border border-slate-200 shrink-0 mt-auto">
        <p className="text-[10px] print:text-[9px] text-slate-500 text-center leading-relaxed">
          <strong>Önemli Not:</strong> Bu rapor öğrencinin LGS öncesi akademik ve kişisel eğilimlerini yansıtan bir rehberlik materyalidir. Kesin lise tercihi için merkezi sınav puanı (yüzdelik dilim) ve öğrencinin ilgi/yetenekleri birlikte değerlendirilmelidir.
        </p>
      </div>

    </div>
  );
};

export default LgsPusulasiReportUI;